import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { del, errorMessage } from "../../lib/api";
import { formatBytes, formatDate } from "../../lib/format";
import { uploadCsv } from "../../lib/upload";
import { Alert, Badge, Button, Card, EmptyState, Loading, PageHeader, StatusBadge, Table, Td } from "../../components/ui";
import { can, useFiles, useWorkspace, useWorkspaceId } from "./hooks";

export function FilesPage() {
  const ws = useWorkspaceId();
  const qc = useQueryClient();
  const detail = useWorkspace(ws);
  const files = useFiles(ws);
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const manage = can(detail.data?.permissions, "connections.manage");

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setMsg(null);
    setProgress(0);
    try {
      const r = await uploadCsv(ws, f, setProgress);
      setMsg({ tone: "ok", text: `Uploaded ${r.file.name}: ${r.sniff.headers.length} columns detected (${r.sniff.encoding}, "${r.sniff.delimiter === "\t" ? "tab" : r.sniff.delimiter}" delimited).` });
      qc.invalidateQueries({ queryKey: ["files", ws] });
    } catch (e) {
      setMsg({ tone: "danger", text: errorMessage(e) });
    } finally {
      setProgress(null);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <>
      <PageHeader
        title="Files"
        subtitle="CSV exports from other platforms (SFMC, Omnisend, Attentive, BigCommerce, WooCommerce, Magento…). Up to 2 GB each."
        actions={
          manage && (
            <>
              <input ref={input} type="file" accept=".csv,.tsv,.txt,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              <Button onClick={() => input.current?.click()} loading={progress !== null}>
                {progress !== null ? `Uploading ${Math.round(progress * 100)}%` : "Upload CSV"}
              </Button>
            </>
          )
        }
      />
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <Card>
        {files.isLoading ? (
          <Loading />
        ) : files.data?.length ? (
          <Table head={["File", "Size", "Columns", "Status", "Uploaded", ""]}>
            {files.data.map((f) => (
              <tr key={f.id}>
                <Td className="font-medium">{f.name}</Td>
                <Td className="tabular">{formatBytes(f.size)}</Td>
                <Td>
                  <div className="flex max-w-md flex-wrap gap-1">
                    {f.sniff?.headers.slice(0, 8).map((h) => (
                      <Badge key={h}>{h}</Badge>
                    ))}
                    {(f.sniff?.headers.length ?? 0) > 8 && <Badge>+{f.sniff!.headers.length - 8}</Badge>}
                  </div>
                </Td>
                <Td>
                  <StatusBadge status={f.status} />
                </Td>
                <Td className="muted">{formatDate(f.createdAt)}</Td>
                <Td className="text-right whitespace-nowrap">
                  {f.status === "ready" && (
                    <Link to="/w/$workspaceId/migrations/new" params={{ workspaceId: ws }} search={{ fileId: f.id }} className="text-accent mr-3 text-sm hover:underline">
                      Migrate
                    </Link>
                  )}
                  {manage && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        if (!confirm(`Delete ${f.name}?`)) return;
                        try {
                          await del(`/api/workspaces/${ws}/files/${f.id}`);
                          qc.invalidateQueries({ queryKey: ["files", ws] });
                        } catch (e) {
                          setMsg({ tone: "danger", text: errorMessage(e) });
                        }
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        ) : (
          <EmptyState title="No files yet" action={manage && <Button onClick={() => input.current?.click()}>Upload CSV</Button>}>
            Upload an export, and we'll detect the format and suggest a migration kit.
          </EmptyState>
        )}
      </Card>
    </>
  );
}
